import React, { FC, useState, useEffect, ChangeEvent, Fragment } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DHDialog from '../dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { toggleTruckIconTemplate } from 'apollo/templates/order/mutation.graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { transportDateHelper } from 'utils/transport-date-helper';
import { Box, Paper } from '@mui/material';
import { Order } from 'models/order';
import { TransportForOrderRequest, TransportServices, TransportStatus } from 'models/transport';
import {
    createTransportForOrderTemplate,
    updateTransportForOrderTemplate,
} from 'apollo/templates/transport/mutation.graphql';
import { Organization } from 'models/organization';
import { getOrderDetailsTemplate } from 'apollo/templates/order';
import Loader from '../loader/loader.component';
import FormattedAddress from '../address/formatted-address.component';

type SchedulePickupFromTruckDialogProps = {
    open: boolean;
    onClose: () => void;
    orderId?: string;
};

// TODO: Needs refactoring
const SchedulePickupFromTruckDialog: FC<SchedulePickupFromTruckDialogProps> = ({
    open,
    onClose,
    orderId,
}: SchedulePickupFromTruckDialogProps) => {
    const { t: translate } = useTranslation(['transport']);
    const currentOrganization = currentOrgVar();
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [minDate, setMinDate] = useState<Date>();
    const [isLabHaveOwnResource, setIsLabHaveOwnResource] = useState<boolean>(false);
    const [comments, setComments] = useState<string>();
    const [internalMessage, setInternalMessage] = useState<string>();
    const [transportRequest, setTransportRequest] = useState<TransportForOrderRequest>();
    const [latestDeliveryDate, setLatestDeliveryDate] = useState<string>(moment().add(1, 'days').format('YYYY-MM-DD'));
    const { showBoundary } = useDenthubErrorBoundary();
    const [order, setOrder] = useState<Order>();

    const [getOrder, { loading }] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getOrder({
            variables: {
                orderId: orderId,
            },
        });
    }, [orderId]);

    const [toggleTruckIcon] = useMutation(toggleTruckIconTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [createTransportForOrder] = useMutation(createTransportForOrderTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateTransportForOrder] = useMutation(updateTransportForOrderTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (order) {
            let date;
            date = moment().add(1, 'days').toDate();
            date = moment(date);
            setMinDate(date.startOf('day').toDate());
            if (order.currentPickup?.transportDate) {
                const transportDate = new Date(order.currentPickup.transportDate);
                setSelectedDate(moment(transportDate).toDate());
                setLatestDeliveryDate(transportDateHelper.nextWorkingDayAsString(transportDate));
            } else {
                const nextDate = transportDateHelper.nextWorkingDay(new Date(), true);
                setSelectedDate(nextDate);
                setLatestDeliveryDate(transportDateHelper.nextWorkingDayAsString(nextDate));
            }
            if (order.currentPickup?.comment) {
                setComments(order.currentPickup.comment);
            }
            if (order.currentPickup?.internalMessage) {
                setInternalMessage(order.currentPickup.internalMessage);
            }
            prepareSelectedPickUpData(order);
        }
    }, [order]);

    const cancel = () => {
        setSelectedDate(undefined);
        setIsLabHaveOwnResource(false);
        setComments(undefined);
        setInternalMessage(undefined);
        setLatestDeliveryDate('');
        onClose();
    };

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date as Date);
        if (transportRequest && date) {
            transportRequest.transportDay = moment(date).format('YYYY-MM-DD');
        }
        setLatestDeliveryDate(transportDateHelper.nextWorkingDayAsString(date));
    };

    const checkOwnResource = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setIsLabHaveOwnResource(checked);
    };

    const onCommentChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setComments(event.target.value);
        if (transportRequest) {
            transportRequest.comment = event.target.value;
        }
    };

    const onInternalMessageChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setInternalMessage(event.target.value);
        if (transportRequest) {
            transportRequest.internalMessage = event.target.value;
        }
    };

    const saveLabHaveOwnResource = async () => {
        if (order) {
            setIsLabHaveOwnResource(false);
            await toggleTruckIcon({ variables: { _id: order._id, truckValue: true, isLabHaveOwnResource: true } });
            onClose();
        }
    };

    const requestTransport = async () => {
        if (transportRequest && selectedDate) {
            transportRequest.transportDay = moment(selectedDate).format('YYYY-MM-DD');
        }
        await createTransportForOrder({ variables: transportRequest });
        onClose();
    };

    const removeTransport = async () => {
        await updateTransportForOrder({
            variables: {
                ...transportRequest,
                userSelectedDate: moment(moment(selectedDate).toDate()).format('YYYY-MM-DD'),
            },
        });
        onClose();
    };

    const prepareSelectedPickUpData = (order: Order) => {
        transportData(order);
        if (order.currentPickup && order.currentPickup._id) {
            setTransportRequest({
                transportOrderId: order.currentPickup._id,
                clinic: (order.currentPickup.clinic as Organization)._id,
                pickup: order?.deliveryDetails?.pickUpLocation,
                comment: order.currentPickup.comment,
                internalMessage: order.currentPickup.internalMessage,
                orderId: order._id,
                manualPackageCount: order.currentPickup.manualPackageCount,
                transportDay: moment(order.currentPickup.transportDate).format('YYYY-MM-DD'),
                dentistName: order.dentist ? order.dentist.fullName : order.created.by.fullName,
                serviceCode: TransportServices.PICKUP,
            });
        }
    };

    const transportData = (selectedOrder: Order) => {
        setTransportRequest({
            clinic: selectedOrder?.deliveryDetails?.pickUpLocation?._id as string,
            pickup: selectedOrder?.deliveryDetails?.pickUpLocation,
            comment: undefined,
            internalMessage: '',
            orderId: selectedOrder?._id,
            manualPackageCount: 0,
            transportDay: moment(selectedDate || new Date()).format('YYYY-MM-DD'),
            dentistName: selectedOrder?.dentist
                ? selectedOrder.dentist?.fullName
                : selectedOrder?.created?.by?.fullName,
            serviceCode: TransportServices.PICKUP,
        });
    };

    return (
        <DHDialog
            maxWidth="md"
            open={open}
            onClose={cancel}
            fullWidth
            title={
                !transportRequest?.transportOrderId
                    ? translate('transport:SCHEDULE_A_NEW_PICK_UP')
                    : translate('transport:SCHEDULED_PICKUP')
            }
            actions={
                <>
                    <Box flexGrow={1}>
                        {order &&
                        transportRequest?.transportOrderId &&
                        order.currentPickup?.status === TransportStatus.PLANNED ? (
                            <Button onClick={removeTransport} color="error">
                                {translate('transport:CANCEL_TRANSPORT')}
                            </Button>
                        ) : undefined}
                    </Box>

                    {!isLabHaveOwnResource ? (
                        <Fragment>
                            {!transportRequest?.transportOrderId ? (
                                <Button
                                    onClick={requestTransport}
                                    autoFocus
                                    disabled={!transportRequest?.transportDay}
                                    variant="contained"
                                    data-test="schedule-pickup-dialog.request-transport-btn"
                                >
                                    {translate('transport:OK')}
                                </Button>
                            ) : undefined}
                        </Fragment>
                    ) : (
                        <Button
                            onClick={saveLabHaveOwnResource}
                            variant="contained"
                            autoFocus
                            data-test="schedule-pickup-dialog.ok-btn"
                        >
                            {translate('transport:OK')}
                        </Button>
                    )}
                </>
            }
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">{translate('transport:PICK_UP_LOCATION')}</Typography>
                    <Paper variant="outlined" square>
                        <Box p={1}>
                            <Box>{transportRequest?.pickup?.name}</Box>
                            <FormattedAddress address={transportRequest?.pickup?.contact.address || {}} />
                        </Box>
                    </Paper>
                    {!transportRequest?.transportOrderId ? (
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isLabHaveOwnResource}
                                        onChange={checkOwnResource}
                                        data-test="schedule-pickup-dialog.own-resource-chk"
                                    />
                                }
                                label={
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                        {translate('transport:DENTHUB_NOT_PICKUP')}
                                    </span>
                                }
                            />
                        </FormGroup>
                    ) : undefined}
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">{translate('transport:PLACE_OF_DELIVERY')}</Typography>
                    <Paper variant="outlined" square>
                        <Box p={1}>
                            <Box>{currentOrganization?.name}</Box>
                            <FormattedAddress address={currentOrganization?.contact?.address || {}} />
                        </Box>
                    </Paper>
                </Grid>

                {transportRequest ? (
                    <Fragment>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">{translate('transport:PICK_UP_DATE')}</Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                                <FormControl fullWidth>
                                    <DatePicker
                                        format={getDatePickerFormat()}
                                        disabled={Boolean(isLabHaveOwnResource || transportRequest?.transportOrderId)}
                                        minDate={new Date()}
                                        shouldDisableDate={transportDateHelper.transportDisabledDates}
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        data-test="schedule-pickup-dialog.pickup-date-picker"
                                    />
                                </FormControl>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">
                                    {translate('transport:LATEST_DELIVERY_DATE')}
                                </Typography>
                                <Paper variant="outlined" square>
                                    <Box p={0.75}>
                                        {translate(
                                            transportDateHelper.transportDateFormatter(latestDeliveryDate, 'date'),
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2">{translate('transport:NO_OF_PACKAGES')}</Typography>
                            {1} {translate('transport:PACKAGE')}
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    disabled={Boolean(isLabHaveOwnResource || transportRequest?.transportOrderId)}
                                    label={translate('transport:COMMENT')}
                                    onChange={onCommentChangeEvent}
                                    value={comments}
                                    multiline
                                    rows={2}
                                    data-test="schedule-pickup-dialog.comment-txt"
                                />
                            </FormControl>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    disabled={Boolean(isLabHaveOwnResource || transportRequest?.transportOrderId)}
                                    label={translate('transport:INTERNAL_MESSAGE')}
                                    onChange={onInternalMessageChangeEvent}
                                    value={internalMessage}
                                    multiline
                                    rows={2}
                                    data-test="schedule-pickup-dialog.internal-message-txt"
                                />
                            </FormControl>
                        </Grid>
                    </Fragment>
                ) : undefined}
            </Grid>

            {loading && <Loader />}
        </DHDialog>
    );
};

export default SchedulePickupFromTruckDialog;
